
import { useState, useEffect, useCallback } from 'react';
import { errorMessage } from '../../../lib/errors';
import { Plus, Search, Wrench, FileText, RefreshCw, Stethoscope, Zap, TrendingUp } from 'lucide-react';
import JobCard from './JobCard';
import QuoteBuilder from '../Quotes/QuoteBuilder';
import CatalogManager from '../Catalog/CatalogManager';
import { useWorkshop, type Job } from '../hooks/useWorkshop';
import EditJobModal from './EditJobModal';
import NewJobFlow from './NewJobFlow';
import WalkaroundCapture from './WalkaroundCapture';
import { InvoiceGenerator } from '../Invoice';
import DiagnosticsPage from './Diagnostics/DiagnosticsPage';
import { ConfirmModal } from '../../../components/ui/ConfirmModal';
import { supabase } from '../../../lib/supabase';
import { useVertical } from '../../../context/VerticalContext';

// Catalog is intentionally hidden from the tab bar (kept in-code for future use).
type Tab = 'jobs' | 'quotes' | 'diagnostics' | 'catalog';

interface JobUsage {
    used: number;
    limit: number; // -1 = unlimited
    tier: string;
}

/** Compact visual badge showing monthly job usage for free-tier orgs */
function JobUsageBadge({ usage, onUpgrade }: { usage: JobUsage | null; onUpgrade?: () => void }) {
    if (!usage || usage.limit === -1) return null; // Unlimited tiers see nothing

    const pct = Math.min((usage.used / usage.limit) * 100, 100);
    const remaining = Math.max(usage.limit - usage.used, 0);
    const isNearLimit = usage.used >= 8;
    const isAtLimit = usage.used >= usage.limit;

    // Colour logic
    const barColour = isAtLimit
        ? 'bg-danger-fill'
        : isNearLimit
            ? 'bg-gold-fill'
            : 'bg-accent';

    const glowClass = isAtLimit
        ? 'shadow-[0_0_12px_color-mix(in_oklab,var(--fd-danger)_40%,transparent)]'
        : isNearLimit
            ? 'shadow-[0_0_12px_var(--fd-gold-glow)]'
            : '';

    return (
        <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-all ${isAtLimit
            ? 'bg-danger/10 border-danger/30'
            : isNearLimit
                ? 'bg-gold-fill/10 border-gold/20'
                : 'bg-tint/5 border-tint/10'
            } ${glowClass}`}
        >
            {/* Icon */}
            <div className={`flex-shrink-0 ${isAtLimit ? 'text-danger' : isNearLimit ? 'text-gold' : 'text-fg-3'}`}>
                <Zap className="w-4 h-4" />
            </div>

            {/* Text + Bar */}
            <div className="flex flex-col gap-1 min-w-[120px]">
                <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-fg-3 uppercase tracking-wider">
                        Jobs this month
                    </span>
                    <span className={`text-xs font-mono font-bold ${isAtLimit ? 'text-danger' : isNearLimit ? 'text-gold' : 'text-fg-2'}`}>
                        {usage.used}/{usage.limit}
                    </span>
                </div>
                {/* Progress bar */}
                <div className="h-1.5 w-full bg-tint/10 rounded-full overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all duration-500 ease-out ${barColour}`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
                {/* Subtext */}
                {isAtLimit ? (
                    <span className="text-[10px] text-danger font-medium">Limit reached</span>
                ) : isNearLimit ? (
                    <span className="text-[10px] text-gold/80">{remaining} remaining</span>
                ) : null}
            </div>

            {/* Upgrade CTA — shown when ≥ 8 jobs */}
            {isNearLimit && onUpgrade && (
                <button
                    onClick={onUpgrade}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-accent to-accent/80 text-fg hover:brightness-110 transition-all flex-shrink-0"
                >
                    <TrendingUp className="w-3 h-3" />
                    Upgrade
                </button>
            )}
        </div>
    );
}

export default function WorkshopHub() {
    const { activeOrgId } = useVertical();
    const [activeTab, setActiveTab] = useState<Tab>('jobs');
    const [actionError, setActionError] = useState<string | null>(null);
    const [filter, setFilter] = useState('');
    const [editingJob, setEditingJob] = useState<Job | null>(null);
    const [invoiceTarget, setInvoiceTarget] = useState<{ jobId: string; existingInvoiceId?: string } | null>(null);
    const [isNewJobModalOpen, setIsNewJobModalOpen] = useState(false);
    const [walkaroundJobId, setWalkaroundJobId] = useState<string | null>(null);
    const [jobToDelete, setJobToDelete] = useState<{ id: string; createdAt: string } | null>(null);
    const [highlightedJobId, setHighlightedJobId] = useState<string | null>(null);
    const [jobUsage, setJobUsage] = useState<JobUsage | null>(null);

    // Use Real Data Hook
    const { jobs, loading, addJob, addJobItem, updateJobStatus, updateJobDetails, deleteJob, refresh } = useWorkshop();

    // Fetch job usage stats
    const refreshUsage = useCallback(async () => {
        if (!activeOrgId) return;
        const { data, error } = await supabase.rpc('fn_get_job_usage', { p_org_id: activeOrgId });
        if (!error && data && Array.isArray(data) && data.length > 0) {
            setJobUsage(data[0] as JobUsage);
        } else if (!error && data && !Array.isArray(data)) {
            setJobUsage(data as unknown as JobUsage);
        }
    }, [activeOrgId]);

    useEffect(() => { refreshUsage(); }, [refreshUsage]);

    // Helper: is the user at their free-tier limit?
    const isAtJobLimit = jobUsage !== null && jobUsage.limit > 0 && jobUsage.used >= jobUsage.limit;

    const filteredJobs = jobs.filter(j =>
        j.customer_name.toLowerCase().includes(filter.toLowerCase()) ||
        j.vehicle_vrm.toLowerCase().includes(filter.toLowerCase())
    );


    // addJob/addJobItem throw when the database refuses the write (free-tier
    // limit, a customer or vehicle from another organisation). Show it.
    const describeWriteError = (err: unknown) =>
        errorMessage(err)?.includes('FREE_TIER_LIMIT_REACHED')
            ? 'You\u2019ve used all your free jobs this month. Upgrade to Pro for unlimited jobs.'
            : errorMessage(err, 'That change could not be saved. Please try again.');

    const handleAddPart = async (jobId: string, jobCreatedAt: string) => {
        try { await addJobItem(jobId, jobCreatedAt, 'part'); }
        catch (err) { setActionError(describeWriteError(err)); }
    };

    const handleAddLabour = async (jobId: string, jobCreatedAt: string) => {
        try { await addJobItem(jobId, jobCreatedAt, 'labour'); }
        catch (err) { setActionError(describeWriteError(err)); }
    };

    const handleComplete = async (jobId: string, jobCreatedAt: string) => {
        await updateJobStatus(jobId, jobCreatedAt, 'ready');
    };

    const handleEditSave = async (jobId: string, jobCreatedAt: string, updates: any) => {
        await updateJobDetails(jobId, jobCreatedAt, updates);
    };

    const handleDeleteJob = async (jobId: string, jobCreatedAt: string) => {
        setJobToDelete({ id: jobId, createdAt: jobCreatedAt });
    };

    const [urlChecked, setUrlChecked] = useState(false);

    // Check for URL params
    const location = window.location;
    useEffect(() => {
        if (urlChecked || loading) return; // Wait for jobs to load

        const params = new URLSearchParams(location.search);
        const customerId = params.get('customer_id');
        const vehicleId = params.get('vehicle_id');
        const jobId = params.get('job_id');
        const tab = params.get('tab');

        // Deep-link into a specific tab (e.g. "+ New Quote" from the dashboard).
        // QuoteBuilder reads the accompanying action=new_quote param itself.
        if (tab === 'quotes' || tab === 'diagnostics' || tab === 'jobs') {
            setActiveTab(tab as Tab);
        }

        // Case 1: Auto-create new job
        if (customerId && vehicleId) {
            const createAutoJob = async () => {
                try {
                    await addJob('AUTO', { customerId, vehicleId });
                } catch (err) {
                    setActionError(describeWriteError(err));
                }
                window.history.replaceState({}, '', '/workshop');
            };
            createAutoJob();
            setUrlChecked(true);
        }

        // Case 2: Open existing job
        else if (jobId) {
            const targetJob = jobs.find(j => j.id === jobId);
            if (targetJob) {
                // ?action=invoice deep-links straight into the invoice generator
                // for this job (the "Invoice" action on the dashboard Job List).
                if (params.get('action') === 'invoice') {
                    setInvoiceTarget({ jobId, existingInvoiceId: targetJob.invoice_id });
                } else {
                    setHighlightedJobId(jobId);
                    // Scroll the job card into view
                    setTimeout(() => {
                        const el = document.getElementById(`job-card-${jobId}`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 150);
                    // Auto-clear highlight after 3 seconds
                    setTimeout(() => setHighlightedJobId(null), 3000);
                }
                // Clear param after opening
                window.history.replaceState({}, '', '/workshop');
                setUrlChecked(true);
            }
        } else {
            setUrlChecked(true);
        }
    }, [urlChecked, loading, addJob, jobs, location.search]);

    // Open modal — blocked if at limit
    const handleNewJob = () => {
        if (isAtJobLimit) return; // Server-side will also reject, but prevent the UI
        setIsNewJobModalOpen(true);
    };

    return (
        <div className="h-full flex flex-col gap-6 p-6">
            {actionError && (
                <div role="alert" className="flex items-start justify-between gap-3 rounded-lg border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
                    <span className="min-w-0 break-words">{actionError}</span>
                    <button
                        type="button"
                        onClick={() => setActionError(null)}
                        aria-label="Dismiss"
                        className="min-h-[44px] min-w-[44px] -my-2 -mr-2 shrink-0 text-danger"
                    >
                        &times;
                    </button>
                </div>
            )}
            {/* Header / Toolbar */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-display font-bold text-fg max-md:text-2xl tracking-tight">Workshop Hub</h1>
                    <p className="text-fg-3 text-sm">Manage jobs, quotes, and inventory</p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    {/* Tabs */}
                    <div className="bg-card border border-line rounded-lg p-1 flex items-center gap-1 overflow-x-auto w-full md:w-auto">
                        <button
                            onClick={() => setActiveTab('jobs')}
                            className={`flex items-center justify-center gap-2 h-10 px-4 rounded-md text-sm font-semibold font-display transition-colors whitespace-nowrap min-w-[100px] flex-1 md:flex-none ${activeTab === 'jobs' ? 'bg-gold-fill/10 text-gold shadow-sheen border border-gold/20' : 'text-fg-3 hover:text-fg hover:bg-raised'}`}
                        >
                            <Wrench className="w-4 h-4" />
                            <span>Jobs</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('quotes')}
                            className={`flex items-center justify-center gap-2 h-10 px-4 rounded-md text-sm font-semibold font-display transition-colors whitespace-nowrap min-w-[100px] flex-1 md:flex-none ${activeTab === 'quotes' ? 'bg-gold-fill/10 text-gold shadow-sheen border border-gold/20' : 'text-fg-3 hover:text-fg hover:bg-raised'}`}
                        >
                            <FileText className="w-4 h-4" />
                            <span>Quotes</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('diagnostics')}
                            className={`flex items-center justify-center gap-2 h-10 px-4 rounded-md text-sm font-semibold font-display transition-colors whitespace-nowrap min-w-[100px] flex-1 md:flex-none ${activeTab === 'diagnostics' ? 'bg-gold-fill/10 text-gold shadow-sheen border border-gold/20' : 'text-fg-3 hover:text-fg hover:bg-raised'}`}
                        >
                            <Stethoscope className="w-4 h-4" />
                            <span>Diagnostics</span>
                        </button>
                    </div>

                    {activeTab === 'jobs' && (
                        <>
                            {/* Job Usage Badge — only visible for free tier */}
                            <JobUsageBadge
                                usage={jobUsage}
                                onUpgrade={() => window.location.href = '/settings'}
                            />

                            <button
                                onClick={activeTab === 'jobs' ? refresh : () => { }}
                                className="flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-card border border-line text-fg-3 hover:text-fg hover:bg-raised transition-colors text-sm font-display font-semibold"
                                title="Refresh"
                            >
                                <RefreshCw className={`w-4 h-4 ${loading && activeTab === 'jobs' ? 'animate-spin' : ''}`} />
                                <span className="hidden sm:inline">Refresh</span>
                            </button>
                            <button
                                onClick={handleNewJob}
                                disabled={isAtJobLimit}
                                className={`flex items-center justify-center gap-2 h-10 px-5 rounded-lg font-semibold font-display text-sm transition-colors whitespace-nowrap shadow-sheen ${
                                    isAtJobLimit
                                        ? 'bg-card text-fg-4 cursor-not-allowed border border-line'
                                        : 'fd-btn-primary cursor-pointer'
                                }`}
                                title={isAtJobLimit ? 'Monthly job limit reached — upgrade to Pro' : 'Create a new job'}
                            >
                                <Plus className="w-4 h-4" />
                                <span>{isAtJobLimit ? 'Limit Reached' : 'New Job'}</span>
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Content Switcher */}
            <div className="flex-1 min-h-0 bg-transparent flex flex-col">
                {activeTab === 'quotes' ? (
                    <QuoteBuilder
                        onJobCreated={() => refresh()}
                        onOpenJob={(jobId) => {
                            setActiveTab('jobs');
                            setHighlightedJobId(jobId);
                            setTimeout(() => {
                                document.getElementById(`job-card-${jobId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 150);
                            setTimeout(() => setHighlightedJobId(null), 3000);
                        }}
                    />
                ) : activeTab === 'diagnostics' ? (
                    <DiagnosticsPage />
                ) : activeTab === 'catalog' ? (
                    <CatalogManager />
                ) : (
                    // JOBS VIEW - LIST ONLY
                    <div className="flex-1 flex flex-col">
                        <div className="relative mb-6">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-4" />
                            <input
                                type="text"
                                placeholder="Filter jobs by name or VRM..."
                                value={filter}
                                onChange={e => setFilter(e.target.value)}
                                className="w-full h-12 bg-card border border-line rounded-xl py-2 pl-11 pr-4 text-base text-fg focus:outline-none focus:border-gold focus:shadow-focus transition-all placeholder:text-fg-4"
                            />
                        </div>

                        {loading && jobs.length === 0 ? (
                            <div className="flex justify-center py-12">
                                <RefreshCw className="w-6 h-6 text-accent animate-spin" />
                            </div>
                        ) : (
                            <div className="space-y-3 w-full overflow-y-auto pr-2 pb-10">
                                {filteredJobs.length === 0 ? (
                                    <div className="text-center py-12 text-fg-4">No jobs found matching filters.</div>
                                ) : (
                                    filteredJobs.map(job => (
                                        <JobCard
                                            key={job.id}
                                            job={job}
                                            viewMode="list"
                                            isHighlighted={highlightedJobId === job.id}
                                            onAddPart={handleAddPart}
                                            onAddLabour={handleAddLabour}
                                            onComplete={handleComplete}
                                            onGenerateInvoice={(jobId, existingInvoiceId) => setInvoiceTarget({ jobId, existingInvoiceId })}
                                            onWalkaround={setWalkaroundJobId}
                                            onDelete={handleDeleteJob}
                                            onRefresh={refresh}
                                        />
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            <EditJobModal
                job={editingJob}
                isOpen={!!editingJob}
                onClose={() => setEditingJob(null)}
                onSave={handleEditSave}
            />

            {/* Invoice Generator Modal */}
            <InvoiceGenerator
                jobId={invoiceTarget?.jobId || ''}
                existingInvoiceId={invoiceTarget?.existingInvoiceId}
                isOpen={!!invoiceTarget}
                onClose={() => {
                    setInvoiceTarget(null);
                    refresh();
                }}
            />

            {/* New Job Modal */}
            {isNewJobModalOpen && (
                <NewJobFlow
                    onClose={() => setIsNewJobModalOpen(false)}
                    onChanged={async () => { await Promise.all([refresh(), refreshUsage()]); }}
                />
            )}

            {/* Walkaround Modal */}
            <WalkaroundCapture
                jobId={walkaroundJobId || ''}
                isOpen={!!walkaroundJobId}
                onClose={() => setWalkaroundJobId(null)}
                onSave={() => setWalkaroundJobId(null)}
            />

            {/* Delete Job Modal */}
            <ConfirmModal
                isOpen={!!jobToDelete}
                onClose={() => setJobToDelete(null)}
                onConfirm={async () => {
                    if (jobToDelete) {
                        await deleteJob(jobToDelete.id, jobToDelete.createdAt);
                        setJobToDelete(null);
                        refreshUsage();
                    }
                }}
                title="Delete Job"
                message="The job will be hidden but still count towards your monthly limit."
                confirmText="Delete"
                isDangerous={true}
            />
        </div >
    );
}
